import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ImageUpload } from "@/components/ImageUpload";
import { toast } from "sonner";

const EMPTY = { name: "", tagline: "", image: "" };
const Lbl = ({ children }) => <label className="text-xs uppercase tracking-[0.15em] text-ash">{children}</label>;

export default function AdminCategories() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/categories").then(({ data }) => setCats(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, tagline: c.tagline || "", image: c.image }); setOpen(true); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.name || !form.image) { toast.error("Nom et image requis."); return; }
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/categories/${editing.slug}`, form);
      else await api.post("/admin/categories", form);
      toast.success(editing ? "Catégorie mise à jour" : "Catégorie créée");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/categories/${toDelete.slug}`);
      toast.success("Catégorie supprimée");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setToDelete(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-ash mb-2">Organisation</p>
          <h1 className="font-serif text-4xl text-ink tracking-tight">Catégories ({cats.length})</h1>
        </div>
        <button data-testid="add-category-btn" onClick={openCreate} className="flex items-center gap-2 bg-ink text-cream px-5 py-3 text-xs uppercase tracking-[0.15em] hover:bg-gold hover:text-ink transition-colors">
          <Plus className="h-4 w-4" /> Ajouter
        </button>
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{[1, 2, 3].map((i) => <div key={i} className="h-40 bg-white border border-stone animate-pulse rounded-sm" />)}</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {cats.map((c) => (
            <div key={c.slug} data-testid={`admin-category-${c.slug}`} className="bg-white border border-stone rounded-sm overflow-hidden">
              <div className="aspect-[16/9] bg-sand overflow-hidden">
                <img src={c.image} alt={c.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-serif text-xl text-ink">{c.name}</h3>
                  <div className="flex gap-1">
                    <button data-testid={`edit-category-${c.slug}`} onClick={() => openEdit(c)} className="p-2 text-graphite hover:text-ink"><Pencil className="h-4 w-4" /></button>
                    <button data-testid={`delete-category-${c.slug}`} onClick={() => setToDelete(c)} className="p-2 text-graphite hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
                <p className="text-sm text-graphite mt-1">{c.tagline}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-cream max-w-md" data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-ink">{editing ? "Modifier la catégorie" : "Nouvelle catégorie"}</DialogTitle>
            <DialogDescription className="text-graphite text-sm">Nom, accroche et visuel de la catégorie.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div><Lbl>Nom</Lbl><Input data-testid="category-name" value={form.name} onChange={set("name")} className="mt-1.5 rounded-sm border-stone bg-white" /></div>
            <div><Lbl>Accroche</Lbl><Input data-testid="category-tagline" value={form.tagline} onChange={set("tagline")} className="mt-1.5 rounded-sm border-stone bg-white" /></div>
            <div><Lbl>Image</Lbl><div className="mt-1.5"><ImageUpload testid="category-image-upload" value={form.image} onChange={(url) => setForm((f) => ({ ...f, image: url }))} /></div></div>
            <DialogFooter>
              <button type="submit" disabled={saving} data-testid="save-category-btn" className="w-full bg-ink text-cream py-3.5 uppercase tracking-[0.15em] text-xs hover:bg-gold hover:text-ink transition-colors disabled:opacity-60">
                {saving ? "Enregistrement..." : editing ? "Mettre à jour" : "Créer"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-cream">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-ink">Supprimer cette catégorie ?</AlertDialogTitle>
            <AlertDialogDescription>« {toDelete?.name} » sera supprimée. Impossible si des produits y sont rattachés.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-category-btn" onClick={confirmDelete} className="rounded-sm bg-destructive hover:bg-destructive/90">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
